/**
 * ChildContext — Shared child selection state for multi-child families
 * 
 * Provides a single source of truth for which child is currently selected
 * across all parent pages (Home, Schedule, Practice, Learn, Read).
 * 
 * Persists selection in localStorage keyed by profile ID.
 * Falls back to first child if no selection or selection is stale.
 * 
 * Usage:
 *   const { selectedChild, children, setSelectedChildId, isMultiChild } = useSelectedChild();
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase/client';
import { useAuth } from '@/lib/auth';
import { useViewAs } from '@/contexts/ViewAsContext';

interface Child {
  id: string;
  name: string;
  in_color_program: boolean;
}

interface ChildContextValue {
  children: Child[];
  selectedChild: Child | null;
  selectedChildId: string | null;
  setSelectedChildId: (id: string) => void;
  isMultiChild: boolean;
  isLoading: boolean;
  refresh: () => Promise<void>;
}

const ChildContext = createContext<ChildContextValue>({
  children: [],
  selectedChild: null,
  selectedChildId: null,
  setSelectedChildId: () => {},
  isMultiChild: false,
  isLoading: true,
  refresh: async () => {},
});

const STORAGE_KEY_PREFIX = 'turn_selected_child_';

export function SelectedChildProvider({ children: childrenProp }: { children: React.ReactNode }) {
  const { profile } = useAuth();
  const { effectiveParentId, isViewingAs } = useViewAs();
  const [childList, setChildList] = useState<Child[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const profileId = profile?.id;

  const loadChildren = useCallback(async () => {
    if (!profileId) {
      setChildList([]);
      setIsLoading(false);
      return;
    }

    try {
      // When admin uses ?viewAs=<parentId>, RLS grants full table access
      // (admin policy). We must explicitly scope to the target parent's
      // children + household children so the preview is accurate.
      let query = supabase
        .from('children')
        .select('id, name, in_color_program')
        .order('created_at', { ascending: true });

      if (isViewingAs && effectiveParentId) {
        query = query.eq('parent_id', effectiveParentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const kids = (data || []) as Child[];
      setChildList(kids);

      // Restore persisted selection or default to first child
      const storageKey = STORAGE_KEY_PREFIX + profileId;
      const storedId = localStorage.getItem(storageKey);
      const validStored = kids.find(c => c.id === storedId);

      if (validStored) {
        setSelectedId(validStored.id);
      } else if (kids.length > 0) {
        setSelectedId(kids[0].id);
        localStorage.setItem(storageKey, kids[0].id);
      } else {
        setSelectedId(null);
      }
    } catch (err) {
      console.error('Failed to load children:', err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [profileId, isViewingAs, effectiveParentId]);

  useEffect(() => {
    loadChildren();
  }, [loadChildren]);

  const setSelectedChildId = useCallback((id: string) => {
    setSelectedId(id);
    if (profileId) {
      localStorage.setItem(STORAGE_KEY_PREFIX + profileId, id);
    }
  }, [profileId]);

  const selectedChild = useMemo(
    () => childList.find(c => c.id === selectedId) || null,
    [childList, selectedId]
  );

  const value = useMemo<ChildContextValue>(() => ({
    children: childList,
    selectedChild,
    selectedChildId: selectedId,
    setSelectedChildId,
    isMultiChild: childList.length > 1,
    isLoading,
    refresh: loadChildren,
  }), [childList, selectedChild, selectedId, setSelectedChildId, isLoading, loadChildren]);

  return (
    <ChildContext.Provider value={value}>
      {childrenProp}
    </ChildContext.Provider>
  );
}

export function useSelectedChild() {
  return useContext(ChildContext);
}
